import { Logger } from '@/lib/log.ts';

const log = Logger('header.navigation');

const touchSupport = 'ontouchstart' in document.documentElement;

let navigationContainer;
let navigation;
let overflowContainer;
let overflowFlyout;
let host;
let breadcrumbSlot: HTMLSlotElement;
let breadcrumbNode: Node;
let breadcrumbWidth = 0;

/**
 *
 *
 * @param root
 */
function adjustNavigation(root: HTMLElement): void {
	const nodes = window.DERSTANDARD?.pageConfig?.nodesv2 || window.DERSTANDARD?.pageConfig?.nodes;

	if (!nodes) {
		return;
	}

	const firstNode = nodes[0]?.node || nodes[0];
	const navItem = root.querySelector(`[href$=${firstNode}]`);

	if (navItem) {
		navItem.parentNode.removeChild(navItem);
	}
}

/**
 * Initialize
 * @param root
 * @returns
 */
function initialize(root: HTMLElement): void {
	if (!root) {
		return;
	}
	log.debug('Initializing navigation script...');

	host = root.getRootNode().host;
	navigationContainer = root;
	navigation = navigationContainer.querySelector('nav');
	overflowContainer = root.querySelector('.app--header--navigation--overflowcontainer');
	overflowFlyout = root.querySelector('.app--header--navigation--overflow');

	breadcrumbSlot = root.querySelector("slot[name='breadcrumb']");
	breadcrumbSlot?.addEventListener('slotchange', () => {
		adjustNavigation(root);
		breadcrumbNode = breadcrumbSlot?.assignedNodes({ flatten: true })[0];
		breadcrumbWidth = breadcrumbNode?.offsetWidth || 0;
	});

	if (touchSupport) {
		host.classList.remove('no-touch');
	} else {
		resize();
	}
}

/**
 * Handle resize of navigation
 */
function resize(): void {
	if (touchSupport) {
		return;
	}

	const overflowContainerWidth: number = overflowContainer?.getAttribute('hidden') ? 0 : overflowContainer?.offsetWidth;
	const items = navigationContainer?.querySelectorAll('.contextual > a') || [];

	const navigationWidth: number = navigation?.offsetWidth || 0;
	const threshold: number = host?.classList.contains('compact') ? 30 : 45;

	let linksWidth = breadcrumbWidth + overflowContainerWidth + threshold;

	if (navigationWidth === 0) {
		return;
	}

	for (const item of items) {
		linksWidth += item.offsetWidth;
	}

	if (linksWidth > navigationWidth && items.length !== 0 && navigationWidth > breadcrumbWidth) {
		while (
			navigationWidth > breadcrumbWidth + overflowContainerWidth &&
			linksWidth > navigationWidth &&
			navigationWidth > breadcrumbWidth + overflowContainerWidth + threshold
		) {
			const lastLink = navigation.querySelector('a:last-of-type');
			const lastLinkWidth = lastLink.offsetWidth;

			log.debug(`Navigation: Moving link to overflow flyout "${lastLink.innerText}"`);

			lastLink.dataset.width = lastLinkWidth;
			overflowFlyout.insertAdjacentElement('afterbegin', lastLink);
			linksWidth -= lastLinkWidth;
		}
	} else {
		while (
			navigationWidth > breadcrumbWidth + overflowContainerWidth &&
			linksWidth <= navigationWidth &&
			overflowFlyout.children.length > 0
		) {
			const firstOverflowLink = overflowFlyout.querySelector('a:first-of-type');
			const firstOverflowLinkWidth = firstOverflowLink?.dataset?.width;

			if (navigationWidth - linksWidth > firstOverflowLinkWidth) {
				log.debug(`Navigation: Restoring link from overflow flyout "${firstOverflowLink.innerText}"`);
				overflowContainer.insertAdjacentElement('beforebegin', firstOverflowLink);
			}
			linksWidth = linksWidth + firstOverflowLinkWidth;
		}
	}

	if (!overflowFlyout.querySelector('a')) {
		overflowContainer.setAttribute('hidden', 'hidden');
	} else {
		overflowContainer.removeAttribute('hidden');
	}
}

const onResize = touchSupport ? null : resize;

export { initialize as initializeNavigation, onResize as onResizeNavigation };
